import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdAdd, MdDelete, MdContentCopy, MdLink, MdQrCode2 } from 'react-icons/md'
import { mask, unMask } from '@/lib/form-state/util'
import { useAuth } from '@/store/hooks'
import {
    getCampaign,
    createCampaign,
    updateCampaign,
    addCampaignLink,
    removeCampaignLink,
    Campaign,
    CampaignLink,
    CreateCampaignData,
    CreateCampaignLinkData,
    getCampaignLinkUrl,
    downloadCampaignLinkQrCode,
} from '@/lib/api/marketing/campaign-api'

const currencyFormat = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) value = '0'
    value = `${value}`.replace(/[^0-9]+/g, '')
    return mask(value, '', 'currency', {
        prefix: 'R$ ',
        decimalSeparator: ',',
        groupSeparator: '.',
        precision: 2,
    })
}

const currencyToNumber = (value: string): number => {
    const raw = unMask(value, 'currency')
    return parseInt(raw, 10) / 100
}

const numberToCurrency = (value: number): string => {
    return currencyFormat(Math.round(value * 100).toString())
}

/**
 * Origens que são anúncio pago. O backend reconhece estes valores (`CAMPAIGN_LINK_AD_SOURCES`)
 * e abre a oportunidade no funil do canal da plataforma, sem `utm_source` no endereço.
 */
const AD_LINK_SOURCES = [
    { value: 'tiktok', label: 'Anúncio do TikTok' },
    { value: 'meta', label: 'Anúncio da Meta (Facebook e Instagram)' },
    { value: 'google', label: 'Anúncio do Google' },
] as const

const CUSTOM_LINK_SOURCE = 'custom'

const linkSourceLabel = (source: string) => AD_LINK_SOURCES.find(s => s.value === source)?.label ?? source

export default function CampaignFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [campaign, setCampaign] = useState<Campaign | null>(null)

    // Form fields
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [status, setStatus] = useState<'DRAFT' | 'ACTIVE' | 'PAUSED' | 'FINISHED'>('DRAFT')
    const [destinationType, setDestinationType] = useState<'WHATSAPP' | 'WHATSAPP_WITH_MESSAGE' | 'URL'>('WHATSAPP')
    const [destinationValue, setDestinationValue] = useState('')
    const [defaultMessage, setDefaultMessage] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [budget, setBudget] = useState('')

    // Links
    const [links, setLinks] = useState<CampaignLink[]>([])
    const [linkSourceOption, setLinkSourceOption] = useState('')
    const [linkSource, setLinkSource] = useState('')
    const [linkMedium, setLinkMedium] = useState('')
    const [linkContent, setLinkContent] = useState('')
    const [addingLink, setAddingLink] = useState(false)

    useEffect(() => {
        if (isEditing && id) {
            loadCampaign(parseInt(id))
        }
    }, [id])

    async function loadCampaign(campaignId: number) {
        setLoadingData(true)
        try {
            const response = await getCampaign(campaignId)
            if (response?.success) {
                const c = response.result
                setCampaign(c)
                setName(c.name)
                setDescription(c.description || '')
                setStatus(c.status)
                // WHATSAPP_WITH_MESSAGE é legado: hoje todo destino WhatsApp tem mensagem.
                setDestinationType(c.defaultDestinationType === 'URL' ? 'URL' : 'WHATSAPP')
                setDestinationValue(c.defaultDestinationValue || '')
                setDefaultMessage(c.defaultMessage || '')
                setStartDate(c.startDate ? c.startDate.substring(0, 10) : '')
                setEndDate(c.endDate ? c.endDate.substring(0, 10) : '')
                setBudget(c.budget ? numberToCurrency(c.budget / 100) : '')
                setLinks(c.links || [])
            }
        } catch {
            toast.error('Erro ao carregar campanha')
            navigate('/marketing/campaigns')
        } finally {
            setLoadingData(false)
        }
    }

    function getDestinationPlaceholder() {
        switch (destinationType) {
            case 'WHATSAPP':
            case 'WHATSAPP_WITH_MESSAGE':
                return '+55 11 99999-9999'
            case 'URL':
                return 'https://...'
            default:
                return ''
        }
    }

    async function handleAddLink() {
        const source = linkSourceOption === CUSTOM_LINK_SOURCE ? linkSource.trim() : linkSourceOption
        if (!source) {
            toast.error('A origem do link é obrigatória')
            return
        }
        if (!id) return

        setAddingLink(true)
        try {
            const data: CreateCampaignLinkData = {
                source,
                medium: linkMedium.trim() || undefined,
                content: linkContent.trim() || undefined,
            }
            const response = await addCampaignLink(parseInt(id), data)
            if (response?.success) {
                setLinks((prev) => [...prev, response.result])
                setLinkSourceOption('')
                setLinkSource('')
                setLinkMedium('')
                setLinkContent('')
                toast.success('Link adicionado com sucesso')
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Erro ao adicionar link'
            toast.error(typeof msg === 'string' ? msg : msg[0])
        } finally {
            setAddingLink(false)
        }
    }

    async function handleRemoveLink(linkId: number) {
        if (!id) return
        if (!window.confirm('Tem certeza que deseja remover este link?')) return

        try {
            await removeCampaignLink(parseInt(id), linkId)
            setLinks((prev) => prev.filter((l) => l.id !== linkId))
            toast.success('Link removido com sucesso')
        } catch {
            toast.error('Erro ao remover link')
        }
    }

    function handleCopyLink(link: CampaignLink) {
        navigator.clipboard.writeText(getCampaignLinkUrl(link))
        toast.success('Link copiado!')
    }

    async function handleDownloadQrCode(link: CampaignLink) {
        if (!id) return
        try {
            await downloadCampaignLinkQrCode(parseInt(id), link)
        } catch {
            toast.error('Erro ao baixar o QR code')
        }
    }

    const isWhatsapp = destinationType !== 'URL'
    // O link acrescenta o código sozinho; o exemplo mostra como a mensagem chega.
    const messagePreview = `${defaultMessage.trim() || 'Olá!'}\n\n(cód. ABC123)`

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()

        if (!name.trim()) {
            toast.error('O nome da campanha é obrigatório')
            return
        }

        setLoading(true)
        try {
            const payload: CreateCampaignData = {
                name: name.trim(),
                description: description.trim() || undefined,
                status,
                defaultDestinationType: destinationType,
                defaultDestinationValue: destinationValue.trim() || undefined,
                // Vazio também vai: é assim que se apaga a mensagem de uma campanha.
                defaultMessage: isWhatsapp ? defaultMessage.trim() : undefined,
                startDate: startDate ? new Date(startDate + 'T00:00:00').toISOString() : undefined,
                endDate: endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined,
                budget: budget ? Math.round(currencyToNumber(budget) * 100) : undefined,
            }

            if (isEditing && id) {
                await updateCampaign(parseInt(id), payload)
                toast.success('Campanha atualizada com sucesso')
            } else {
                await createCampaign(payload)
                toast.success('Campanha criada com sucesso')
            }

            navigate('/marketing/campaigns')
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Erro ao salvar campanha'
            toast.error(typeof msg === 'string' ? msg : msg[0])
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    return (
        <div className="max-w-4xl mx-auto p-4 space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate('/marketing/campaigns')}
                >
                    <MdArrowBack className="text-lg" />
                </button>
                <h1 className="text-2xl font-bold">
                    {isEditing ? 'Editar Campanha' : 'Nova Campanha'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Informações básicas */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 space-y-4">
                    <h2 className="text-lg font-semibold">Informações da Campanha</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Nome */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Nome da campanha"
                            />
                        </div>

                        {/* Status */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Status</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={status}
                                onChange={(e) =>
                                    setStatus(e.target.value as 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'FINISHED')
                                }
                            >
                                <option value="DRAFT">Rascunho</option>
                                <option value="ACTIVE">Ativa</option>
                                <option value="PAUSED">Pausada</option>
                                <option value="FINISHED">Finalizada</option>
                            </select>
                        </div>
                    </div>

                    {/* Descrição */}
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">Descrição</span>
                        </label>
                        <textarea
                            className="textarea textarea-bordered w-full"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Descrição da campanha (opcional)"
                            rows={3}
                        />
                    </div>
                </div>

                {/* Destino e Mensagem */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 space-y-4">
                    <h2 className="text-lg font-semibold">Destino</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Tipo de destino */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Para onde o link leva</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={destinationType}
                                onChange={(e) => setDestinationType(e.target.value as 'WHATSAPP' | 'URL')}
                            >
                                <option value="WHATSAPP">WhatsApp</option>
                                <option value="URL">Página na internet (URL)</option>
                            </select>
                        </div>

                        {/* Valor do destino */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    {isWhatsapp ? 'Número do WhatsApp' : 'Endereço da página'}
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                value={destinationValue}
                                onChange={(e) => setDestinationValue(e.target.value)}
                                placeholder={getDestinationPlaceholder()}
                            />
                        </div>
                    </div>

                    {isWhatsapp && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Mensagem que o contato envia</span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    value={defaultMessage}
                                    onChange={(e) => setDefaultMessage(e.target.value)}
                                    placeholder="Ex.: Olá! Vi o anúncio e quero saber mais."
                                    rows={4}
                                    maxLength={500}
                                />
                                <span className="label-text-alt text-base-content/60 mt-1.5">
                                    Já vem escrita no WhatsApp de quem clica. Vazia, vai só "Olá!".
                                </span>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Como chega na conversa</span>
                                </label>
                                <div className="rounded-lg bg-base-200 p-3 text-sm whitespace-pre-wrap break-words min-h-[6.5rem]">
                                    {messagePreview}
                                </div>
                                <span className="label-text-alt text-base-content/60 mt-1.5">
                                    O código no fim é colocado sozinho e muda a cada clique: é ele que liga a
                                    conversa à campanha. Sem o código, a conversa não conta para a campanha.
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Período e Orçamento */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 space-y-4">
                    <h2 className="text-lg font-semibold">Período e Orçamento</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Data início */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Data de início</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        {/* Data término */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Data de término</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Orçamento */}
                    <div className="form-control w-full max-w-xs">
                        <label className="label">
                            <span className="label-text font-medium">Orçamento</span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            value={budget}
                            onChange={(e) => setBudget(currencyFormat(e.target.value))}
                            placeholder="R$ 0,00"
                        />
                    </div>
                </div>

                {/* Links da Campanha (apenas em edição) */}
                {isEditing && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <MdLink className="text-xl text-secondary" />
                            <h2 className="text-lg font-semibold">Links da Campanha</h2>
                        </div>
                        <div className="divider mt-0 mb-2" />

                        {/* Lista de links existentes */}
                        {links.length > 0 ? (
                            <div className="space-y-2">
                                {links.map((link) => (
                                    <div
                                        key={link.id}
                                        className="flex items-center gap-3 p-3 rounded-lg border border-base-300/50 hover:bg-base-200/50 transition-colors"
                                    >
                                        <span className="badge badge-secondary badge-sm">
                                            {linkSourceLabel(link.source)}
                                        </span>
                                        {link.medium && (
                                            <span className="badge badge-ghost badge-sm">
                                                {link.medium}
                                            </span>
                                        )}
                                        <span className="text-sm flex-1 truncate text-base-content/70">
                                            {getCampaignLinkUrl(link)}
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs"
                                            onClick={() => handleCopyLink(link)}
                                            title="Copiar link"
                                        >
                                            <MdContentCopy className="text-base" />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs"
                                            onClick={() => handleDownloadQrCode(link)}
                                            title="Baixar QR code"
                                        >
                                            <MdQrCode2 className="text-base" />
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-xs text-error"
                                            onClick={() => handleRemoveLink(link.id)}
                                            title="Remover link"
                                        >
                                            <MdDelete className="text-base" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-base-content/50 text-center py-4">
                                Nenhum link adicionado
                            </p>
                        )}

                        {/* Formulário para adicionar link */}
                        <div className="border border-dashed border-base-300 rounded-lg p-4 space-y-3">
                            <h3 className="text-sm font-medium text-base-content/70">
                                Adicionar novo link
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="form-control w-full">
                                    <label className="label py-1">
                                        <span className="label-text text-sm">
                                            Origem <span className="text-error">*</span>
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered select-sm w-full"
                                        value={linkSourceOption}
                                        onChange={(e) => setLinkSourceOption(e.target.value)}
                                    >
                                        <option value="" disabled>
                                            Selecione...
                                        </option>
                                        {AD_LINK_SOURCES.map((s) => (
                                            <option key={s.value} value={s.value}>
                                                {s.label}
                                            </option>
                                        ))}
                                        <option value={CUSTOM_LINK_SOURCE}>Outra origem</option>
                                    </select>
                                    {linkSourceOption === CUSTOM_LINK_SOURCE && (
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm w-full mt-2"
                                            value={linkSource}
                                            onChange={(e) => setLinkSource(e.target.value)}
                                            placeholder="bio do instagram, panfleto, e-mail..."
                                        />
                                    )}
                                </div>
                                <div className="form-control w-full">
                                    <label className="label py-1">
                                        <span className="label-text text-sm">Meio</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        value={linkMedium}
                                        onChange={(e) => setLinkMedium(e.target.value)}
                                        placeholder="social, cpc, print..."
                                    />
                                </div>
                                <div className="form-control w-full">
                                    <label className="label py-1">
                                        <span className="label-text text-sm">Conteúdo</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full"
                                        value={linkContent}
                                        onChange={(e) => setLinkContent(e.target.value)}
                                        placeholder="variação A/B..."
                                    />
                                </div>
                            </div>
                            {AD_LINK_SOURCES.some((s) => s.value === linkSourceOption) && (
                                <p className="text-xs text-base-content/60">
                                    Use este link como destino do anúncio. A conversa que vier por ele abre a
                                    oportunidade no funil do tráfego pago da plataforma, sem precisar acrescentar
                                    nada ao endereço.
                                </p>
                            )}
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    className="btn btn-outline btn-sm"
                                    onClick={handleAddLink}
                                    disabled={addingLink}
                                >
                                    {addingLink ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdAdd className="text-base" />
                                    )}
                                    Adicionar Link
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/marketing/campaigns')}
                    >
                        Cancelar
                    </button>
                    <button type="submit" className="btn btn-secondary" disabled={loading}>
                        {loading ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        {isEditing ? 'Salvar Alterações' : 'Criar Campanha'}
                    </button>
                </div>
            </form>
        </div>
    )
}
